/**
 * "Wie ben je" — tap your face.
 *
 * This is the whole account model as the children experience it: no password,
 * no login, no typing. The device was let in once with a QR code; from then on
 * picking who you are is one tap.
 *
 * Being honest about what that is and is not: on a shared iPad anyone can tap
 * any face. That is deliberate. This selects an identity for the scoreboard, it
 * does not authenticate a person, and nothing here should ever be treated as if
 * it did.
 *
 * The typed name stays as a fallback — for a guest at the table, and for the
 * evening the server cannot be reached.
 */

import { useEffect, useState } from 'react';
import Avatar from '@/components/Avatar';
import { api } from '@/lib/api';

export interface Chosen {
  name: string;
  profileId: string | null;
}

interface Profile {
  id: string;
  name: string;
  emoji: string;
  avatar: string | null;
}

export default function WhoAreYou({
  value,
  onChange,
}: {
  value: Chosen;
  onChange: (chosen: Chosen) => void;
}) {
  const [profiles, setProfiles] = useState<Profile[] | null>(null);
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    // Never blocks: no answer means the typed field, which is what this screen
    // was before players existed.
    void api<Profile[]>('/api/profiles')
      .then(setProfiles)
      .catch(() => setProfiles([]));
  }, []);

  const known = profiles ?? [];
  const showTyped = typing || (profiles !== null && known.length === 0);

  return (
    <div className="space-y-3">
      {known.length > 0 && (
        <ul className="grid grid-cols-4 gap-3 sm:grid-cols-6">
          {known.map((profile) => {
            const chosen = value.profileId === profile.id;
            return (
              <li key={profile.id}>
                <button
                  type="button"
                  aria-pressed={chosen}
                  onClick={() => {
                    setTyping(false);
                    onChange({ name: profile.name, profileId: profile.id });
                  }}
                  className="flex w-full flex-col items-center gap-1"
                >
                  <span
                    className={`rounded-full transition ${
                      chosen
                        ? 'ring-2 ring-accent-400 ring-offset-2 ring-offset-base-800'
                        : 'opacity-70'
                    }`}
                  >
                    <Avatar player={profile} size={56} />
                  </span>
                  <span
                    className={`w-full truncate text-center text-xs ${
                      chosen ? 'text-white' : 'text-slate-400'
                    }`}
                  >
                    {profile.name}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {showTyped ? (
        <input
          value={value.profileId ? '' : value.name}
          onChange={(event) => onChange({ name: event.target.value, profileId: null })}
          placeholder="Of typ een naam"
          autoComplete="given-name"
          maxLength={20}
          autoFocus={typing}
          className="min-h-12 w-full rounded-xl border border-white/10 bg-base-900 px-3 text-base outline-none focus:border-accent-500"
        />
      ) : (
        known.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setTyping(true);
              onChange({ name: '', profileId: null });
            }}
            className="text-sm text-slate-500 underline underline-offset-4 hover:text-slate-300"
          >
            Iemand anders
          </button>
        )
      )}
    </div>
  );
}
